import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Maximize2, Pause, Play } from 'lucide-react';

/**
 * Product screenshot gallery, thumbnail row + full-width active shot.
 * Each shot has an optional caption. Keeps the "actual product" feel the
 * brief calls for, without hidden slideshows or heavy image assets.
 *
 * Auto-rotates every `interval` ms when `autoplay` is true. Auto-play is
 * paused on hover, when the lightbox is open, when the user manually
 * navigates, and when the tab is hidden. Respects prefers-reduced-motion.
 */
const ScreenshotGallery = ({
  shots = [],
  eyebrow = 'Inside the platform',
  title,
  description,
  testid,
  autoplay = true,
  interval = 6000,
}) => {
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [paused, setPaused] = useState(false);
  const hoverRef = useRef(false);

  const next = () => setActive((i) => (i + 1) % Math.max(shots.length, 1));
  const prev = () => setActive((i) => (i - 1 + Math.max(shots.length, 1)) % Math.max(shots.length, 1));

  // Auto-rotate
  useEffect(() => {
    if (!autoplay || paused || zoom || shots.length <= 1) return;
    const reduced = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    const id = setInterval(() => {
      if (!hoverRef.current && document.visibilityState !== 'hidden') {
        setActive((i) => (i + 1) % shots.length);
      }
    }, interval);
    return () => clearInterval(id);
  }, [autoplay, paused, zoom, shots.length, interval]);

  if (!shots.length) return null;
  const current = shots[active];

  return (
    <section
      className="py-16 md:py-20 bg-slate-950 text-white"
      data-testid={testid || 'screenshot-gallery'}
      onMouseEnter={() => { hoverRef.current = true; }}
      onMouseLeave={() => { hoverRef.current = false; }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.22em] text-blue-400 font-semibold mb-4">{eyebrow}</p>
        {title && <h2 className="text-3xl md:text-4xl font-medium tracking-tight leading-tight">{title}</h2>}
        {description && <p className="mt-3 text-slate-400 max-w-3xl">{description}</p>}

        <div className="mt-10 relative bg-slate-900 rounded-2xl p-2 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] ring-1 ring-white/5">
          <img
            src={current.src}
            alt={current.alt || current.caption || 'Product screenshot'}
            loading="lazy"
            className="w-full aspect-[16/9] object-cover object-top rounded-lg cursor-zoom-in"
            onClick={() => setZoom(true)}
          />

          {/* Auto-rotate progress bar */}
          {autoplay && !paused && !zoom && shots.length > 1 && (
            <div className="absolute top-2 left-2 right-2 h-0.5 bg-white/10 rounded overflow-hidden">
              <div
                key={active}
                className="h-full bg-blue-400"
                style={{ animation: `videra-gallery-progress ${interval}ms linear forwards` }}
              />
            </div>
          )}

          <div className="absolute inset-x-0 bottom-2 flex items-center justify-between px-3">
            <button onClick={() => { prev(); setPaused(true); }} className="w-8 h-8 rounded-full bg-slate-900/70 backdrop-blur border border-white/10 flex items-center justify-center hover:bg-slate-800" data-testid="gallery-prev" aria-label="Previous">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPaused((p) => !p)}
                className="text-xs bg-slate-900/70 backdrop-blur border border-white/10 rounded-full px-3 py-1.5 hover:bg-slate-800 inline-flex items-center gap-1.5"
                data-testid="gallery-playpause"
                aria-label={paused ? 'Play slideshow' : 'Pause slideshow'}
              >
                {paused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
                {paused ? 'Play' : 'Pause'}
              </button>
              <button onClick={() => setZoom(true)} className="text-xs bg-slate-900/70 backdrop-blur border border-white/10 rounded-full px-3 py-1.5 hover:bg-slate-800 inline-flex items-center gap-1.5">
                <Maximize2 className="w-3 h-3" /> View full
              </button>
            </div>
            <button onClick={() => { next(); setPaused(true); }} className="w-8 h-8 rounded-full bg-slate-900/70 backdrop-blur border border-white/10 flex items-center justify-center hover:bg-slate-800" data-testid="gallery-next" aria-label="Next">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {current.caption && (
          <p className="mt-4 text-sm text-slate-400" data-testid="gallery-caption">{current.caption}</p>
        )}

        <div className="mt-6 grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2">
          {shots.map((s, i) => (
            <button
              key={s.src}
              onClick={() => { setActive(i); setPaused(true); }}
              className={`aspect-video rounded-md overflow-hidden border-2 transition-all ${i === active ? 'border-blue-400' : 'border-transparent opacity-60 hover:opacity-100'}`}
              data-testid={`gallery-thumb-${i}`}
              aria-label={`View screenshot ${i + 1}`}
            >
              <img src={s.src} alt={s.alt || ''} loading="lazy" className="w-full h-full object-cover object-top" />
            </button>
          ))}
        </div>
      </div>
      {zoom && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setZoom(false)} data-testid="gallery-lightbox">
          <img src={current.src} alt={current.alt || ''} className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}
      <style>{`@keyframes videra-gallery-progress { from { width: 0% } to { width: 100% } }`}</style>
    </section>
  );
};

export default ScreenshotGallery;
